/**
 * How a thread's activity list absorbs one activity.
 *
 * An activity is identified by its id, and the same id is reported more than
 * once on purpose: a compaction reaches the server as four or five provider
 * events (start, result, boundary, the `PostCompact` recap, the token snapshot
 * that settles `postTokens`) and the tracker folds all of them onto a single
 * activity so the chat shows one row. Appending instead of replacing turns that
 * fold into one repeated row per event — which is exactly what the timeline
 * showed after a `/compact`, until the live client learned to upsert like the
 * projector always did.
 *
 * Shared because both sides apply the same stream: the server projector folds
 * it into the read model, and the web store folds it into live state between
 * snapshots. Only one of them having the rule is a UI that disagrees with the
 * page it renders after a reload.
 */

/**
 * Replaces the entry that already carries this id, or appends it when it is
 * new.
 *
 * The replacement keeps the entry's position: callers that care about ordering
 * sort afterwards, and the ones that don't (the live client) would otherwise
 * see a row jump to the bottom of the transcript every time it was updated.
 * Returns the original array when nothing changed, so identity comparisons
 * upstream can skip re-rendering.
 */
export function upsertThreadActivity<A extends { readonly id: string }>(
  activities: ReadonlyArray<A>,
  activity: A,
): ReadonlyArray<A> {
  const index = activities.findIndex((entry) => entry.id === activity.id);
  if (index === -1) {
    return [...activities, activity];
  }
  if (activities[index] === activity) {
    return activities;
  }
  const next = [...activities];
  next[index] = activity;
  return next;
}
